import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { contact } from "../mock";
import Reveal from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sentReply, setSentReply] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success("Thank you, your message has been sent.");
      setSentReply(
        data?.reply ||
          "Thank you for reaching out. We have received your message and will respond in due course."
      );
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-center bg-cover bw" style={{ backgroundImage: `url(${contact.bg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/85 via-[#0c0b08]/82 to-[#0c0b08]" />
        <div className="grain" />
        <div className="container-x max-w-3xl text-center relative z-10">
          <Reveal><p className="eyebrow mb-6">{contact.eyebrow}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="font-display uppercase text-5xl md:text-7xl text-foreground leading-none">
              {contact.title}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-foreground/75 mt-6">{contact.subtitle}</p>
          </Reveal>
        </div>
      </section>

      {/* BODY */}
      <section className="section-pad bg-[#0c0b08]">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Form */}
          <Reveal>
            <form onSubmit={handleSubmit} className="bg-[#131009] border border-border rounded-md p-8 md:p-10">
              <h2 className="font-display uppercase text-2xl text-foreground mb-8">Send us a message</h2>

              {sentReply && (
                <div className="mb-8 rounded-md border border-gold/40 bg-[#0d0b09] p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="text-gold" size={16} />
                    <p className="eyebrow">A note from Melano</p>
                  </div>
                  <p className="font-serif-accent italic text-foreground/90 leading-relaxed">{sentReply}</p>
                  <button
                    type="button"
                    onClick={() => setSentReply(null)}
                    className="mt-4 text-xs uppercase tracking-[0.18em] font-display text-gold hover:brightness-110 transition"
                  >
                    Write another message
                  </button>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="eyebrow block mb-3">Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-gold transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-gold transition-colors"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-3">Message</label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-border rounded-sm px-4 py-3 text-foreground focus:outline-none focus:border-gold transition-colors resize-none"
                    placeholder="How can we help?"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-melano-purple text-white font-display uppercase font-bold tracking-[0.2em] text-sm py-4 rounded-sm hover:brightness-110 transition disabled:opacity-60"
                >
                  {loading ? "Sending\u2026" : "Send"}
                </button>
              </div>
            </form>
          </Reveal>

          {/* Details */}
          <Reveal delay={120}>
            <div className="flex flex-col justify-center h-full">
              <div className="space-y-10">
                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 rounded-full border border-gold/50 flex items-center justify-center shrink-0">
                    <Mail className="text-gold" size={18} />
                  </div>
                  <div>
                    <p className="eyebrow mb-2">Email</p>
                    <a href={`mailto:${contact.email}`} className="text-foreground hover:text-gold transition-colors block">
                      {contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-11 h-11 rounded-full border border-gold/50 flex items-center justify-center shrink-0">
                    <Phone className="text-gold" size={18} />
                  </div>
                  <div>
                    <p className="eyebrow mb-2">Phone</p>
                    <a href={contact.phoneHref} className="text-foreground hover:text-gold transition-colors">
                      {contact.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
